/**
 * One step of a run on its read-only canvas (Feature 71, 7.3): drawn by
 * where it stands, with the task it ran as, or why it did not run. `first`
 * is drawn dark, as on the editor, for the task whose completion started
 * the run. The handles are only anchors for the links.
 */
import type { LinkageStepStatus } from '@optum/api-client/types';

import { slotText, viewerTimeZone } from '../../lib/cron.js';
import { shortId } from '../../lib/format.js';
import { Icon } from '../../ui/finixt.js';
import { Handle, Position, type Node, type NodeProps } from '../../ui/flow.js';
import { NODE_WIDTH } from './draft.js';
import { RETURN_IN, RETURN_OUT } from './StepNode.js';
import type { RunFlowNode } from './run-flow.js';

export type RunNodeData = RunFlowNode['data'] & Record<string, unknown>;
export type RunNodeType = Node<RunNodeData, 'run'>;

const ANCHOR = { opacity: 0, width: 1, height: 1, minWidth: 0, minHeight: 0, border: 'none' };

interface Tone {
  border: string;
  background: string;
  color: string;
}

const QUIET: Tone = {
  border: '1px dashed var(--border-input)',
  background: 'var(--surface-sunken)',
  color: 'var(--text-faint)',
};

const FIRST_TONE: Tone = {
  border: '1px solid var(--fill-action)',
  background: 'var(--fill-action)',
  color: 'var(--text-ghost)',
};

// A schedule run's `first` is its first task, with the schedule's violet outline (Feature 80 D18).
const FIRE_TONE: Tone = {
  border: '1.5px solid var(--violet)',
  background: 'var(--fill-action)',
  color: 'var(--text-ghost)',
};

// A call run's `first` is the call, outlined in its color as on the editor's canvas (Feature 78).
const CALL_TONE: Tone = {
  border: '1.5px solid var(--info)',
  background: 'var(--surface-card)',
  color: 'var(--info)',
};

const TONE: Record<LinkageStepStatus, Tone> = {
  completed: { border: '1px solid var(--ok)', background: 'var(--ok-tint)', color: 'var(--ok)' },
  failed: {
    border: '1px solid var(--danger)',
    background: 'var(--danger-tint)',
    color: 'var(--danger)',
  },
  running: {
    border: '1px solid var(--info)',
    background: 'var(--info-tint)',
    color: 'var(--info)',
  },
  waiting: {
    border: '1px solid var(--border-input)',
    background: 'var(--surface-card)',
    color: 'var(--text-soft)',
  },
  skipped: QUIET,
  cancelled: QUIET,
};

/**
 * The node's last two lines: where it stands, then the task it ran as or
 * why it did not run. Two lines, not one, so a task id never wraps.
 */
export function stateLines(data: RunNodeData): [string, string | null] {
  if (data.status === 'first' && data.firedFor !== undefined) {
    return ['fired', data.firedFor ? slotText(new Date(data.firedFor), viewerTimeZone()) : null];
  }
  if (data.status === 'first' && data.call) return ['the call', null];
  if (data.status === 'first') return ['step one done', null];
  return [data.status, data.taskId ? shortId(data.taskId) : data.reason];
}

export function RunNode({ data }: NodeProps<RunNodeType>) {
  const first = data.status === 'first';
  const fire = first && data.firedFor !== undefined;
  const call = first && Boolean(data.call);
  const tone = fire
    ? FIRE_TONE
    : call
      ? CALL_TONE
      : data.status === 'first'
        ? FIRST_TONE
        : TONE[data.status];
  // Only a task's or a fire's `first` is drawn dark.
  const dark = first && !call;
  const [status, detail] = stateLines(data);
  return (
    <div
      title={data.failureReason ?? undefined}
      style={{
        width: NODE_WIDTH,
        boxSizing: 'border-box',
        padding: '9px 11px',
        borderRadius: 'var(--radius-card)',
        background: tone.background,
        border: tone.border,
        color: dark ? 'var(--text-inverse)' : 'var(--text-strong)',
        cursor: data.taskId ? 'pointer' : 'default',
        opacity: data.dimmed ? 0.45 : 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {!first && (
        <Handle type="target" position={Position.Left} isConnectable={false} style={ANCHOR} />
      )}
      <span
        className="mono"
        style={{ fontSize: 'var(--text-body-md)', fontWeight: 'var(--weight-semibold)' }}
      >
        {data.name}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 'var(--text-meta-lg)',
          color: dark ? 'var(--text-ghost)' : 'var(--text-soft)',
        }}
      >
        {data.capabilityId}
      </span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 'var(--text-meta-lg)',
          fontWeight: 'var(--weight-semibold)',
          color: tone.color,
        }}
      >
        {call && <Icon name="call_split" size={14} />}
        {status}
      </span>
      {detail && (
        <span className="mono" style={{ fontSize: 'var(--text-meta-lg)', color: tone.color }}>
          {detail}
        </span>
      )}
      {data.mode === 'write_back' && (
        <span className="mono" style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--violet)' }}>
          write-back → {data.target}
        </span>
      )}
      {data.mode === 'independent' && (
        <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--info)' }}>independent</span>
      )}
      <Handle type="source" position={Position.Right} isConnectable={false} style={ANCHOR} />
      <Handle
        id={RETURN_OUT}
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        style={ANCHOR}
      />
      <Handle
        id={RETURN_IN}
        type="target"
        position={Position.Bottom}
        isConnectable={false}
        style={ANCHOR}
      />
    </div>
  );
}
